'use client';

import { setAttr } from '@directus/visual-editing';

import { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Star } from 'lucide-react';
import Container from '@/components/ui/container';
import { cn, parseHeading } from '@/lib/utils';
import { DirectusFile } from '@/types/directus-schema';
import { useGoogleReviews } from '@/context/GoogleReviewsContext';

// Fallback data if CMS data is missing
const DEFAULT_HERO_IMAGES = [
	{
		src: '/images/airconditioning-installatie-regio-lansingerland-en-omstreken.png',
		alt: 'Airconditioning installatie regio Lansingerland',
	},
	{
		src: '/images/breuer-airc-installatie-rotterdam-erasmusbrug.webp',
		alt: 'Airco installatie in Lansingerland en omstreken voor particulier en zakelijk',
	},
	{
		src: '/images/airco-onderhoud.png',
		alt: 'Airco onderhoud',
	},
	{
		src: '/images/klimaattechniek-bedrijfspanden.png',
		alt: 'Airco bedrijfspanden installatie en onderhoud',
	},
	{
		src: '/images/specialist-in-airco-en-klimaattechniek.png',
		alt: 'Specialist in airco en klimaattechniek',
	},
];

interface HeroProps {
	data: {
		headline?: string;
		tagline?: string;
		cta_text?: string;
		cta_link?: string;
		hero_images?: { directus_files_id: DirectusFile }[]; // M2M structure
	};
	itemId?: string;
	blockId?: string;
}

export default function Hero({ data, itemId, blockId }: HeroProps) {
	const [currentIndex, setCurrentIndex] = useState(0);
	const googleData = useGoogleReviews();

	const title = data?.headline || 'Geniet morgen al van \neen perfect binnenklimaat.';
	const subtitle = data?.tagline || 'De hoogst beoordeelde airco-specialist van Lansingerland.';
	const ctaText = data?.cta_text || 'Vraag direct uw vrijblijvende offerte aan';
	const ctaLink = data?.cta_link || '/offerte';



	// Process images: handle Directus M2M file objects or use default
	const processedImages = Array.isArray(data?.hero_images)
		? data.hero_images
			.map((img) => {
				const file = img.directus_files_id;
				const directusUrl = process.env.NEXT_PUBLIC_DIRECTUS_URL;

				if (!file?.filename_disk) return null;

				return {
					src: `${directusUrl}/assets/${file.filename_disk}`,
					alt: file.title || 'Hero image',
				};
			})
			.filter((img): img is { src: string; alt: string } => Boolean(img))
		: [];

	const imagesToDisplay = processedImages.length > 0 ? processedImages : DEFAULT_HERO_IMAGES;

	useEffect(() => {
		const interval = setInterval(() => {
			setCurrentIndex((prevIndex) => (prevIndex + 1) % imagesToDisplay.length);
		}, 5000);

		return () => clearInterval(interval);
	}, [imagesToDisplay.length]);

	return (
		<section className="relative h-[600px] md:h-[700px] overflow-hidden bg-gray-900">
			{/* Background Slider */}
			<div className="absolute inset-0 size-full">
				{imagesToDisplay.map((image, index) => (
					<div
						key={image.src}
						className={cn(
							'absolute inset-0 size-full transition-opacity duration-1000 ease-in-out',
							index === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0',
						)}
					>
						<Image src={image.src} alt={image.alt} fill className="object-cover" priority={index === 0} sizes="100vw" />
						{/* Gradient overlay for better text contrast */}
						<div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
					</div>
				))}
			</div>

			{/* Content */}
			<div className="relative z-20 h-full flex items-center">
				<Container className="w-full">
					<div className="max-w-4xl space-y-8 text-white md:ml-12">
						<h1
							className="text-4xl md:text-5xl lg:text-7xl font-heading leading-tight drop-shadow-md whitespace-pre-line"
							{...(itemId
								? {
									'data-directus': setAttr({
										collection: 'block_hero',
										item: itemId,
										fields: ['headline'],
									}),
								}
								: {})}
						>
							{parseHeading(title)}
						</h1>

						<p
							className="text-lg md:text-xl text-gray-200 drop-shadow-sm max-w-xl"
							{...(itemId
								? {
									'data-directus': setAttr({
										collection: 'block_hero',
										item: itemId,
										fields: ['tagline'],
									}),
								}
								: {})}
						>
							{parseHeading(subtitle)}
						</p>

						{/* Ratings */}
						<div className="flex items-center space-x-2">
							<div className="flex text-yellow-400">
								{[1, 2, 3, 4, 5].map((i) => (
									<Star key={i} className="size-5 md:size-6 pill-current fill-yellow-400" />
								))}
							</div>
							<span className="text-sm md:text-base font-medium text-white drop-shadow-md">
								{googleData.rating.toFixed(1)}/5 sterren, op basis van <a href="https://maps.google.com/?cid=4464300248615167290&g_mp=CiVnb29nbGUubWFwcy5wbGFjZXMudjEuUGxhY2VzLkdldFBsYWNlEAIYBCAA" target="_blank" rel="noreferrer" className="underline hover:no-underline hover:text-white/80 transition-colors">{googleData.reviewCount}+ Google reviews</a>
							</span>
						</div>

						{/* CTA */}
						<div className="pt-4">
							<Button
								size="lg"
								className="bg-orange hover:bg-orange-hover text-white text-lg px-8 py-7 rounded-md shadow-xl w-full md:w-auto transition-transform hover:scale-105"
								asChild
							>
								<Link href={ctaLink}>
									<span
										{...(itemId
											? {
												'data-directus': setAttr({
													collection: 'block_hero',
													item: itemId,
													fields: ['cta_text'],
												}),
											}
											: {})}
									>
										{ctaText}
									</span>
								</Link>
							</Button>
							<p className="text-xs md:text-sm text-gray-300 mt-3 drop-shadow-sm">
								Binnen 2 werkdagen een reactie — Geheel vrijblijvend
							</p>
						</div>
					</div>
				</Container>
			</div>
		</section>
	);
}
